import {
  reactQueryGet,
  reactQueryPost,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type { PaginationHelper } from "@circle-react/types";
import { queryOptions } from "react-query";

export interface PartnerProgramAnalytics {
  all_signups: number | null;
  conversions: number | null;
  revenue_cents: number | null;
}

export interface PartnerProgramEarningsTrendPoint {
  amount_cents: number;
  date: string;
}

export interface PartnerProgramReferral {
  email: string;
  joined_on: string;
  plan_name: string | null;
  status: "active" | "trial" | "cancelled";
  your_earnings_cents: number | null;
}

export interface PartnerProgramDetails {
  analytics: PartnerProgramAnalytics;
  available_now_cents: number;
  available_soon_cents: number;
  earnings_trend: PartnerProgramEarningsTrendPoint[];
  enrolled: boolean;
  next_payout_date: string | null;
  paid_cents: number;
  partner_link: string | null;
  total_earned_cents: number;
}

export const REFERRALS_PER_PAGE = 20;

export type PartnerProgramReferralsOrder = Record<string, "asc" | "desc">;

interface PartnerProgramReferralsParams {
  order?: PartnerProgramReferralsOrder;
  page?: number;
  perPage?: number;
}

export const partnerProgramKey = createQueryKeyFactory("partner-program");

export const partnerProgramMutationKeys = {
  enroll: partnerProgramKey.key("enroll"),
};

export const partnerProgramQueries = {
  details: () =>
    queryOptions<PartnerProgramDetails>({
      queryKey: partnerProgramKey.key("details"),
      queryFn: () => reactQueryGet(internalApi.partnerProgram.get()),
    }),
  referrals: ({
    order,
    page = 1,
    perPage = REFERRALS_PER_PAGE,
  }: PartnerProgramReferralsParams = {}) =>
    queryOptions<PaginationHelper<PartnerProgramReferral>>({
      queryKey: partnerProgramKey.key("referrals", perPage, page, order ?? {}),
      queryFn: () =>
        reactQueryGet(
          internalApi.partnerProgram.referrals({
            page,
            per_page: perPage,
            ...(order && { order }),
          }),
        ),
    }),
};

export const partnerProgramInvalidations = {
  all: () =>
    queryClient.invalidateQueries({ queryKey: partnerProgramKey.root() }),
};

export const enrollInPartnerProgram = () =>
  reactQueryPost<PartnerProgramDetails, Record<string, never>>(
    internalApi.partnerProgram.enroll(),
    {},
  );

export const partnerProgramMutations = {
  enroll: () => ({
    mutationKey: partnerProgramMutationKeys.enroll,
    mutationFn: enrollInPartnerProgram,
    onSuccess: (details: PartnerProgramDetails) => {
      queryClient.setQueryData(
        partnerProgramQueries.details().queryKey,
        details,
      );
      return partnerProgramInvalidations.all();
    },
  }),
};
